import React, { useState, useEffect, useCallback, useRef } from "react";
import { Helmet } from "react-helmet";
import html2pdf from "html2pdf.js";
import Icon from "../../components/AppIcon";
import { useAuth } from "../../contexts/AuthContext";
import { useTenantContext } from "../../contexts/TenantContext";
import { policyGeneratorApi } from "../../services/api";

/**
 * Employee-facing Policy Page
 * Read-only view of the current TRONC policy document.
 * Employees see the policy for their tenant. Admins are redirected to /policy-generator.
 */
const PolicyPage = () => {
  const { user } = useAuth();
  const { selectedTenant, needsTenantSelection, isGlobalAdmin } = useTenantContext();

  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const printRef = useRef(null);

  const fetchPolicy = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await policyGeneratorApi.generate();
      const data = response?.data ?? response?.Data ?? response;
      if (data) {
        setPolicy(data);
    } else {
        setError("No policy data returned.");
      }
    } catch (err) {
      console.error("[PolicyPage] Error:", err);
      setError(err.message || "Failed to load policy document.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTenant || (!isGlobalAdmin && !needsTenantSelection)) {
      fetchPolicy();
    }
  }, [selectedTenant?.id, fetchPolicy, isGlobalAdmin, needsTenantSelection]);

  const handleDownloadPdf = useCallback(() => {
    if (!policy || !printRef.current) return;

    const filename = `TRONC-Policy-${policy.tenantSlug || "document"}-${new Date().toISOString().split("T")[0]}.pdf`;

  html2pdf()
      .set({
        margin: [15, 15, 15, 15],
        filename,
      image: { type: "jpeg", quality: 0.98 },
   html2canvas: { scale: 2, useCORS: true, letterRendering: true },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
     pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      })
      .from(printRef.current)
      .save()
      .catch((err) => console.error("PDF generation failed:", err));
  }, [policy]);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
  year: "numeric",
    });
  };

  // Loading
  if (loading) {
    return (
      <>
        <Helmet><title>TRONC Policy - Rocket Tronc</title></Helmet>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
     <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading policy document...</p>
      </div>
        </div>
 </>
    );
  }

  // Error
  if (error) {
    return (
      <>
    <Helmet><title>TRONC Policy - Rocket Tronc</title></Helmet>
        <div className="max-w-3xl mx-auto p-6">
   <div className="card p-8 text-center">
            <Icon name="AlertTriangle" size={48} className="text-warning mx-auto mb-4" />
            <h3 className="font-semibold mb-2">Unable to Load Policy</h3>
   <p className="text-muted-foreground mb-4">{error}</p>
            <button onClick={fetchPolicy} className="btn btn-primary">
         <Icon name="RefreshCw" size={18} />
         Try Again
    </button>
          </div>
        </div>
    </>
    );
  }

  if (!policy) return null;

  const p = policy;

  return (
<>
      <Helmet>
        <title>TRONC Policy - {p.tenantName || "Rocket Tronc"}</title>
      </Helmet>

    <div className="max-w-4xl mx-auto p-6 space-y-6">
    {/* Header */}
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
  <div>
            <h1 className="text-2xl font-bold">TRONC Policy</h1>
      <p className="text-muted-foreground">
  Current distribution policy for {p.tenantName}
     </p>
 </div>
          <button onClick={handleDownloadPdf} className="btn btn-primary btn-sm">
   <Icon name="Download" size={16} />
            Download PDF
</button>
      </div>

      {/* Last generated info */}
        <div className="bg-info/5 border border-info/20 rounded-lg p-4 flex items-start gap-3">
 <Icon name="Info" size={18} className="text-info mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-info">Policy Document</p>
   <p className="text-muted-foreground">
              Generated on {formatDate(p.generatedAt)}. This document reflects the current system configuration.
            </p>
          </div>
        </div>

        <div ref={printRef} className="space-y-6">
          {/* Organisation Section */}
          <div className="card">
         <div className="card-header">
        <div className="flex items-center gap-3">
  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon name="Building" size={20} className="text-primary" />
   </div>
          <h2 className="card-title">Organisation</h2>
        </div>
 </div>
    <div className="card-content">
         {p.organisation?.logoUrl && (
     <div className="mb-4">
         <img
src={p.organisation.logoUrl}
           alt={p.organisation?.companyName}
          className="h-12 w-auto"
        onError={(e) => { e.target.style.display = "none"; }}
      />
       </div>
          )}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
           <div>
   <p className="text-sm text-muted-foreground">Company Name</p>
           <p className="font-medium">{p.organisation?.companyName}</p>
</div>
         <div>
             <p className="text-sm text-muted-foreground">Contact Email</p>
           <p className="font-medium">{p.organisation?.contactEmail}</p>
   </div>
                {p.organisation?.contactPhone && (
         <div>
          <p className="text-sm text-muted-foreground">Contact Phone</p>
     <p className="font-medium">{p.organisation.contactPhone}</p>
      </div>
    )}
   {p.organisation?.address && (
       <div className="md:col-span-2">
         <p className="text-sm text-muted-foreground">Address</p>
        <p className="font-medium">{p.organisation.address}</p>
  </div>
      )}
        </div>
        </div>
   </div>

          {/* Distribution Method */}
  <div className="card">
            <div className="card-header">
  <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
               <Icon name="PieChart" size={20} className="text-primary" />
         </div>
       <h2 className="card-title">Distribution Method</h2>
              </div>
      </div>
       <div className="card-content">
              {p.distributionMethod?.activeRuleName ? (
        <div className="space-y-4">
   <div className="flex items-center gap-2">
      <span className="badge badge-success">Active</span>
               <span className="font-semibold">{p.distributionMethod.activeRuleName}</span>
            <span className="text-sm text-muted-foreground">({p.distributionMethod.activeRuleType})</span>
      </div>
      {p.distributionMethod.activeRuleDescription && (
      <p className="text-sm text-muted-foreground">{p.distributionMethod.activeRuleDescription}</p>
         )}
               <div className="bg-muted rounded-lg p-4 text-sm">
            {p.distributionMethod.methodDescription}
         </div>

       {/* Department allocations */}
       {p.distributionMethod.departmentAllocations?.length > 0 && (
       <div>
           <h4 className="text-sm font-semibold mb-2">Department Allocations</h4>
    <div className="space-y-2">
 {p.distributionMethod.departmentAllocations.map((alloc, i) => (
   <div key={i} className="flex items-center justify-between py-1">
 <span className="text-sm">{alloc.department}</span>
   <span className="font-semibold text-sm">{alloc.percentage}%</span>
       </div>
               ))}
          </div>
   </div>
       )}

                  {/* Options */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div className={`flex items-center gap-2 p-2 rounded ${p.distributionMethod.enableMinimumFloor ? "bg-success/10" : "bg-muted"}`}>
    <Icon name={p.distributionMethod.enableMinimumFloor ? "Check" : "X"} size={14} />
      <span>Min Floor {p.distributionMethod.enableMinimumFloor ? `£${p.distributionMethod.minimumFloorAmount}` : "Off"}</span>
    </div>
   <div className={`flex items-center gap-2 p-2 rounded ${p.distributionMethod.enableHourFactor ? "bg-success/10" : "bg-muted"}`}>
         <Icon name={p.distributionMethod.enableHourFactor ? "Check" : "X"} size={14} />
           <span>Hour Factor {p.distributionMethod.enableHourFactor ? `(${(p.distributionMethod.hourFactorWeight * 100).toFixed(0)}%)` : "Off"}</span>
        </div>
            <div className={`flex items-center gap-2 p-2 rounded ${p.distributionMethod.enableSeniorityFactor ? "bg-success/10" : "bg-muted"}`}>
     <Icon name={p.distributionMethod.enableSeniorityFactor ? "Check" : "X"} size={14} />
       <span>Seniority {p.distributionMethod.enableSeniorityFactor ? `(${(p.distributionMethod.seniorityFactorWeight * 100).toFixed(0)}%)` : "Off"}</span>
               </div>
     </div>
      </div>
     ) : (
          <div className="text-center py-4">
       <Icon name="AlertCircle" size={24} className="text-muted-foreground mx-auto mb-2" />
       <p className="text-muted-foreground">No active distribution rule configured.</p>
       </div>
      )}
 </div>
</div>

          {/* Eligibility Criteria */}
        <div className="card">
         <div className="card-header">
        <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon name="UserCheck" size={20} className="text-primary" />
      </div>
        <h2 className="card-title">Eligibility Criteria</h2>
              </div>
     </div>
            <div className="card-content">
          <div className="divide-y divide-border">
        {p.eligibilityCriteria?.criteria?.map((c, i) => (
        <div key={i} className="flex items-start gap-3 py-2">
        <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
             c.enabled ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"
  }`}>
        <Icon name={c.enabled ? "Check" : "X"} size={14} />
    </div>
    <div>
      <p className="font-medium text-sm">{c.name}</p>
      <p className="text-xs text-muted-foreground">{c.description}</p>
          </div>
         </div>
            ))}
              </div>
        </div>
          </div>

          {/* Job Roles */}
          <div className="card">
     <div className="card-header">
           <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
         <Icon name="Briefcase" size={20} className="text-primary" />
     </div>
           <h2 className="card-title">Job Roles</h2>
         </div>
          </div>
       <div className="card-content">
   <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-success" />
         Eligible Roles ({p.jobRoles?.eligibleRoles?.length || 0})
          </h3>
      <div className="space-y-2">
                    {p.jobRoles?.eligibleRoles?.map((role, i) => (
  <div key={i} className="flex items-center justify-between py-1.5 px-3 bg-success/5 rounded text-sm">
     <div>
 <span className="font-medium">{role.name}</span>
          {role.department && <span className="text-xs text-muted-foreground ml-2">{role.department}</span>}
              </div>
      <span className="text-xs font-mono">{role.isManagement ? 'Mgmt' : 'Staff'}</span>
             </div>
  ))}
          </div>
    </div>
         <div>
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
    <span className="w-2 h-2 rounded-full bg-muted-foreground" />
            Ineligible Roles ({p.jobRoles?.ineligibleRoles?.length || 0})
       </h3>
        <div className="space-y-2">
    {p.jobRoles?.ineligibleRoles?.map((role, i) => (
         <div key={i} className="flex items-center justify-between py-1.5 px-3 bg-muted rounded text-sm">
          <span>{role.name}</span>
        {role.department && <span className="text-xs text-muted-foreground">{role.department}</span>}
     </div>
       ))}
     {(!p.jobRoles?.ineligibleRoles || p.jobRoles.ineligibleRoles.length === 0) && (
    <p className="text-sm text-muted-foreground italic">All roles are eligible.</p>
  )}
    </div>
    </div>
        </div>
  </div>
          </div>

 {/* Compliance Notes */}
   {p.complianceNotes?.length > 0 && (
       <div className="card">
      <div className="card-header">
    <div className="flex items-center gap-3">
       <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
    <Icon name="Shield" size={20} className="text-primary" />
 </div>
       <h2 className="card-title">Compliance Notes</h2>
  </div>
    </div>
     <div className="card-content">
         <div className="space-y-2">
   {p.complianceNotes.map((note, i) => (
  <div key={i} className="flex items-start gap-2 text-sm">
      <Icon
       name={note.startsWith("WARNING") ? "AlertTriangle" : note.startsWith("NOTE") ? "Info" : "CheckCircle"}
        size={14}
          className={`mt-0.5 flex-shrink-0 ${
                note.startsWith("WARNING") ? "text-error" : note.startsWith("NOTE") ? "text-warning" : "text-success"
            }`}
     />
    <span className="text-muted-foreground">{note}</span>
    </div>
  ))}
         </div>
     </div>
       </div>
  )}
        </div>

  {/* Help Section */}
   <div className="card p-6 bg-muted/30">
       <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
    <Icon name="HelpCircle" size={20} className="text-primary" />
            </div>
     <div>
       <h4 className="font-semibold mb-2">Need Help?</h4>
     <p className="text-sm text-muted-foreground">
 If you have questions about the TRONC policy or believe the eligibility criteria don't apply
                correctly to your situation, please contact your manager or the Troncmaster.
       </p>
    </div>
          </div>
        </div>
  </div>
    </>
  );
};

export default PolicyPage;
